import React, { useState } from 'react'
import {
	AVAILABLE_COLORS,
	AVAILABLE_TOOLS,
	AvailableColor,
	BUFFER_COLOR,
	mixColors,
	ToolTypes,
} from './helpers'
import { CloseAnnotationTypes } from '@mission.io/question-views'
import { SelectableCircle } from './TargetAnnotationCreator'
import { FaEraser } from 'react-icons/fa'
import { ANNOTATION_INFORMATION_METHODS } from './StudentAnnotation'
import { getSingleAnnotationStyle } from './annotationStyling'

export const DEFAULT_TOOLS: Record<
	string,
	Array<CloseAnnotationTypes.AvailableColor>
> = {}
AVAILABLE_TOOLS.forEach(tool => {
	DEFAULT_TOOLS[tool] = [...AVAILABLE_COLORS]
})

/**
 * useTestingTools - a hook which returns a UI for selecting tools as well as the current tool selection
 *
 * @return {
 * 	currentAnnotationInformation: CloseAnnotationTypes.AnnotationInformation, // the current tool selection
 *  toolSelector: JSX.Element, // the UI for selecting the tool
 * }
 */
export function useTestingTools(
	availableAnnotationStyles: Record<
		CloseAnnotationTypes.ToolTypes,
		Array<CloseAnnotationTypes.AvailableColor>
	> = DEFAULT_TOOLS
): {
	currentAnnotationInformation: CloseAnnotationTypes.AnnotationInformation
	toolSelector: JSX.Element
} {
	const [selectedTool, setSelectedTool] = useState<ToolTypes | null>(
		// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		(Object.keys(
			availableAnnotationStyles
		) as CloseAnnotationTypes.ToolTypes[])[0]
	)
	const [selectedColor, setSelectedColor] = useState<AvailableColor | null>(
		// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		selectedTool ? availableAnnotationStyles[selectedTool]?.[0] : null
	)
	const [eraserType, setEraserType] = useState<
		| 'none'
		| CloseAnnotationTypes.AnnotationInformationMethods['ERASE_WHOLE_ANNOTATION']
		| CloseAnnotationTypes.AnnotationInformationMethods['ERASE_RANGE']
	>('none')

	let currentAnnotationInformation: CloseAnnotationTypes.AnnotationInformation = null
	if (eraserType === 'none' && selectedColor != null && selectedTool != null) {
		currentAnnotationInformation = {
			color: selectedColor,
			tool: selectedTool,
			method: ANNOTATION_INFORMATION_METHODS.ANNOTATE,
		}
	} else if (
		eraserType === ANNOTATION_INFORMATION_METHODS.ERASE_WHOLE_ANNOTATION
	) {
		currentAnnotationInformation = {
			color: null,
			tool: null,
			method: eraserType,
		}
	} else if (eraserType === ANNOTATION_INFORMATION_METHODS.ERASE_RANGE) {
		currentAnnotationInformation = {
			color: null,
			tool: null,
			method: eraserType,
		}
	}

	return {
		currentAnnotationInformation,
		toolSelector: (
			<>
				<div className="flex flex-row gap-2 mb-2">
					Color:{' '}
					<div className="flex flex-row justify-center items-center gap-2">
						{selectedTool &&
							availableAnnotationStyles[selectedTool].map(color => (
								<SelectableCircle
									disabled={eraserType != 'none'}
									style={{
										backgroundColor: mixColors([color]) ?? undefined,
									}}
									onClick={() => {
										if (selectedColor === color) {
											if (eraserType != 'none') {
												setSelectedColor(null)
											}
											return
										}

										setSelectedColor(color)
									}}
									selected={selectedColor === color}
									key={color}
								/>
							))}
					</div>
				</div>
				<div className="flex flex-row gap-2 mb-2">
					Tools:{' '}
					<div className="flex flex-row justify-center items-center gap-2">
						{(Object.keys(availableAnnotationStyles) as Array<
							CloseAnnotationTypes.ToolTypes
						>).map(tool => (
							<SelectableCircle
								disabled={eraserType != 'none'}
								className={`flex flex-row items-center bg-gray-c justify-center text-xs text-black`}
								onClick={() => {
									if (selectedTool === tool) {
										if (eraserType != 'none') {
											setSelectedTool(null)
										}
										return
									}

									setSelectedTool(tool)
								}}
								key={tool}
								selected={tool === selectedTool}>
								<div
									style={getSingleAnnotationStyle({
										style: {
											tool,
											color: selectedColor ?? BUFFER_COLOR,
										},
									})}
									className={`!decoration-2`}>
									&nbsp;A&nbsp;
								</div>
							</SelectableCircle>
						))}
					</div>
				</div>
				<div className="flex flex-row gap-2">
					{/* Erasers:{' '}  See comment below: */}
					{([
						ANNOTATION_INFORMATION_METHODS.ERASE_WHOLE_ANNOTATION,
						/* 
						// This is commented out on purpose!
						We do not want to display this eraser to the creator since students do not have access to it! 
						If we every allow them to use the range eraser, then we can add this back in.
						ANNOTATION_INFORMATION_METHODS.ERASE_RANGE, 
						*/
					] as const).map(eraserOption => (
						<SelectableCircle
							className={`flex flex-row items-center bg-gray-c justify-center text-xs text-black max-w-full px-2`}
							onClick={() => {
								if (eraserType === eraserOption) {
									if (selectedTool === null) {
										// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
										setSelectedTool(AVAILABLE_TOOLS[0])
									}
									if (selectedColor === null) {
										// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
										setSelectedColor(AVAILABLE_COLORS[0])
									}
									setEraserType('none')
									return
								}
								setEraserType(eraserOption)
							}}
							selected={eraserOption === eraserType}
							key={eraserOption}>
							<FaEraser />
							&nbsp; Erase{' '}
							{eraserOption
								.split('_')
								.slice(1)
								.join(' ')
								.toLowerCase()}
						</SelectableCircle>
					))}
				</div>
			</>
		),
	}
}
